import { Alert } from '@amore/ui/components/alert'
import { Badge } from '@amore/ui/components/badge'
import { Button } from '@amore/ui/components/button'
import { ViewToggle, ViewToggleItem } from '@amore/ui/components/view-toggle'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { CalendarRange, List, Plus } from 'lucide-react'
import { type ReactNode, useState } from 'react'
import { plural, TODAY } from '@/lib/format'
import { replaceById, useCanEdit, useUpdate, useWedding } from '@/mock/store'
import type { Task } from '@/mock/types'
import { type Column, DataTable, type TableGroup } from '@/patterns/data-table'
import { EmptyState } from '@/patterns/empty-state'
import { FilterBar } from '@/patterns/filter-bar'
import { Screen } from '@/patterns/screen'
import { StatSummary } from '@/patterns/stat-summary'
import { useToast } from '@/shell/toaster'
import tasksIllustration from './assets/tasks.svg'
import {
  byDueThenPriority,
  filterLabels,
  inPhase,
  matchesFilter,
  matchesSearch,
  parseFilter,
  parseView,
  phaseRange,
  type TaskFilter,
  type TaskView,
  taskFilters,
  weddingPhases,
} from './model'
import { TaskSheet } from './task-sheet'
import { CompleteToggle, TaskTable, taskColumns } from './task-table'

export function TasksScreen() {
  const data = useWedding()
  const canEdit = useCanEdit()
  const update = useUpdate()
  const { showToast } = useToast()
  const navigate = useNavigate()
  const search = useSearch({ strict: false }) as { filter?: string; q?: string; view?: string }
  const { taskId } = useParams({ strict: false }) as { taskId?: string }
  const filter = parseFilter(search.filter)
  const query = search.q ?? ''
  const view = parseView(search.view)
  const [adding, setAdding] = useState(false)

  const setSearch = (next: { filter?: TaskFilter; q?: string; view?: TaskView }) => {
    const merged = { filter, q: query, view, ...next }
    void navigate({
      to: taskId ? `/tasks/${taskId}` : '/tasks',
      search: {
        ...(merged.filter !== 'all' ? { filter: merged.filter } : {}),
        ...(merged.q ? { q: merged.q } : {}),
        ...(merged.view !== 'list' ? { view: merged.view } : {}),
      },
      replace: true,
    })
  }
  const listSearch = {
    ...(filter !== 'all' ? { filter } : {}),
    ...(query ? { q: query } : {}),
    ...(view !== 'list' ? { view } : {}),
  }
  const openTask = (task: Task) => void navigate({ to: `/tasks/${task.id}`, search: listSearch })
  const closeTask = () => void navigate({ to: '/tasks', search: listSearch })

  const setDone = (task: Task, done: boolean) => {
    update((current) => ({ ...current, tasks: replaceById(current.tasks, task.id, { done }) }))
    if (done)
      showToast({
        title: 'Úkol dokončen',
        description: task.title,
        tone: 'success',
        action: {
          label: 'Vrátit zpět',
          altText: 'Vrátit úkol mezi nesplněné',
          onClick: () =>
            update((current) => ({
              ...current,
              tasks: replaceById(current.tasks, task.id, { done: false }),
            })),
        },
      })
    else showToast({ title: 'Úkol vrácen mezi nesplněné' })
  }

  const tasks = data.tasks
  const openTaskRecord = taskId ? tasks.find((task) => task.id === taskId) : undefined
  const headerAction =
    canEdit && tasks.length > 0 ? (
      <Button onClick={() => setAdding(true)}>
        <Plus className="size-4" aria-hidden="true" /> Přidat úkol
      </Button>
    ) : null

  if (tasks.length === 0)
    return (
      <Screen
        title="Úkoly"
        illustration={<img src={tasksIllustration} alt="" className="h-auto w-full" />}
        description="Mějte přehled o tom, co je potřeba zařídit a co už je hotové."
      >
        <EmptyState
          icon={<img src={tasksIllustration} alt="" className="h-28 w-32" />}
          title="Zatím tu nejsou žádné úkoly"
          description={
            canEdit
              ? 'Přidejte první úkol a začněte mít svatební přípravy pod kontrolou.'
              : 'Až vznikne první úkol, zobrazí se tady.'
          }
          actions={
            canEdit ? <Button onClick={() => setAdding(true)}>Přidat první úkol</Button> : null
          }
        />
        {adding ? <TaskSheet onClose={() => setAdding(false)} /> : null}
      </Screen>
    )

  const counts = Object.fromEntries(
    taskFilters.map((value) => [value, tasks.filter((task) => matchesFilter(task, value)).length]),
  ) as Record<TaskFilter, number>
  const percent = Math.round((counts.done / tasks.length) * 100)
  const toggleFilter = (value: TaskFilter) =>
    setSearch({ filter: filter === value ? 'all' : value })

  const visible = tasks
    .filter((task) => matchesFilter(task, filter) && matchesSearch(task, query))
    .sort(byDueThenPriority)
  const columns = taskColumns(data)
  const rowAction = (task: Task) => (
    <CompleteToggle task={task} canEdit={canEdit} onChange={(done) => setDone(task, done)} />
  )
  const noResults = (
    <div className="space-y-3">
      <p className="font-semibold text-foreground">Žádné úkoly v tomto přehledu</p>
      <p className="text-sm">Zkuste zobrazit všechny úkoly nebo hledat jinak.</p>
      <Button
        appearance="outline"
        tone="neutral"
        size="sm"
        onClick={() => setSearch({ filter: 'all', q: '' })}
      >
        Zobrazit všechny úkoly
      </Button>
    </div>
  )

  return (
    <Screen
      title="Úkoly"
      illustration={<img src={tasksIllustration} alt="" className="h-auto w-full" />}
      description="Mějte přehled o tom, co je potřeba zařídit a co už je hotové."
      actions={headerAction}
    >
      <StatSummary
        groups={[
          {
            title: 'Dokončené úkoly',
            value: `${percent} %`,
            progress: {
              value: counts.done,
              max: tasks.length,
              tone: 'success',
              label: `Hotovo ${counts.done} z ${tasks.length} úkolů`,
            },
            tiles: [
              {
                label: 'Hotovo',
                value: counts.done,
                caption: `z ${tasks.length} ${plural(tasks.length, 'úkolu', 'úkolů', 'úkolů')}`,
                tone: 'success',
                onSelect: () => toggleFilter('done'),
                active: filter === 'done',
              },
              {
                label: 'Zbývá',
                value: counts.open,
                caption: 'Včetně úkolů bez termínu',
                onSelect: () => toggleFilter('open'),
                active: filter === 'open',
              },
            ],
          },
          {
            title: 'Nejbližší termíny',
            tiles: [
              {
                label: 'Po termínu',
                value: counts.overdue,
                tone: counts.overdue > 0 ? 'danger' : 'default',
                onSelect: () => toggleFilter('overdue'),
                active: filter === 'overdue',
              },
              {
                label: 'Do 14 dnů',
                value: counts.upcoming,
                tone: counts.upcoming > 0 ? 'warning' : 'default',
                onSelect: () => toggleFilter('upcoming'),
                active: filter === 'upcoming',
              },
            ],
          },
        ]}
      />

      <FilterBar
        filters={taskFilters.map((value) => ({
          value,
          label: filterLabels[value],
          count: counts[value],
        }))}
        value={filter}
        onChange={(value) => setSearch({ filter: value })}
        search={{
          value: query,
          onChange: (value) => setSearch({ q: value }),
          placeholder: 'Hledat úkol',
        }}
        actions={
          <ViewToggle aria-label="Zobrazení úkolů">
            <ViewToggleItem
              icon={<List aria-hidden="true" />}
              pressed={view === 'list'}
              onClick={() => setSearch({ view: 'list' })}
            >
              Seznam
            </ViewToggleItem>
            <ViewToggleItem
              icon={<CalendarRange aria-hidden="true" />}
              pressed={view === 'timeline'}
              onClick={() => setSearch({ view: 'timeline' })}
            >
              Časová osa
            </ViewToggleItem>
          </ViewToggle>
        }
      />

      {view === 'timeline' ? (
        data.identity.date ? (
          <TimelineTable
            tasks={visible}
            weddingDate={data.identity.date}
            columns={columns}
            onOpen={openTask}
            rowAction={rowAction}
            empty={noResults}
          />
        ) : (
          <EmptyState
            icon={<CalendarRange aria-hidden="true" />}
            title="Časová osa potřebuje datum svatby"
            description="Úkoly rozdělíme podle toho, kolik zbývá do vašeho dne: šest měsíců před svatbou, poslední měsíc, poslední týden…"
            actions={
              <Button onClick={() => void navigate({ to: '/' })}>Doplnit datum svatby</Button>
            }
          />
        )
      ) : (
        <TaskTable
          tasks={visible}
          grouped={filter === 'all' || filter === 'open'}
          columns={columns}
          onOpen={openTask}
          rowAction={rowAction}
          empty={noResults}
        />
      )}

      {taskId && !openTaskRecord ? <Alert>Úkol není dostupný.</Alert> : null}
      {openTaskRecord ? (
        <TaskSheet key={openTaskRecord.id} task={openTaskRecord} onClose={closeTask} />
      ) : null}
      {adding ? <TaskSheet onClose={() => setAdding(false)} /> : null}
    </Screen>
  )
}

/**
 * "Časová osa": tasks by phase of the run-up, counted back from the wedding
 * date. The current phase is marked; finished phases collapse.
 */
function TimelineTable({
  tasks,
  weddingDate,
  columns,
  onOpen,
  rowAction,
  empty,
}: {
  tasks: Task[]
  weddingDate: string
  columns: Column<Task>[]
  onOpen: (task: Task) => void
  rowAction: (task: Task) => ReactNode
  empty: ReactNode
}) {
  const phases = weddingPhases(weddingDate)
  const groups: TableGroup<Task>[] = phases.flatMap((phase) => {
    const rows = tasks.filter((task) => task.dueDate !== null && inPhase(task.dueDate, phase))
    const current = inPhase(TODAY, phase)
    if (rows.length === 0 && !current) return []
    const done = rows.filter((task) => task.done).length
    return [
      {
        id: phase.id,
        label: (
          <span className="flex items-center gap-2">
            {phase.label}
            {current ? <Badge tone="accent">Teď jste tady</Badge> : null}
          </span>
        ),
        meta: `${phaseRange(phase)} · ${
          rows.length === 0 ? 'zatím bez úkolů' : `hotovo ${done} z ${rows.length}`
        }`,
        rows,
        collapsible: true,
        defaultCollapsed: rows.length > 0 && done === rows.length,
      },
    ]
  })
  const undated = tasks.filter((task) => task.dueDate === null)
  if (undated.length > 0)
    groups.push({
      id: 'undated',
      label: 'Bez termínu',
      meta: count(undated.length),
      rows: undated,
      collapsible: true,
    })
  return (
    <DataTable
      columns={columns}
      getRowId={(task) => task.id}
      onRowClick={onOpen}
      rowActions={rowAction}
      rowMuted={(task) => task.done}
      empty={empty}
      aria-label="Úkoly podle času do svatby"
      groups={groups}
    />
  )
}

const count = (value: number) => `${value} ${plural(value, 'úkol', 'úkoly', 'úkolů')}`
